import React, { useEffect, useState } from 'react'
import {
  Button,
  PageHeader,
  Form,
  Input,
  Row,
  Col,
  notification,
  Card,
  Table,
  Modal,
} from 'antd'
import {
  getProductPrice,
  updateProductPrices,
} from '../../network/ProductPrices'
import { useParams } from 'react-router-dom'

const EditProductPrice = ({ history }) => {
  const [productData, setProductData] = useState(null)
  const [pricing, setPricing] = useState([])
  const [adjustments, setAdjustments] = useState([])
  const [currency, setCurrency] = useState('')
  const [form] = Form.useForm()
  const { key, storeId } = useParams()

  useEffect(() => {
    getProductPrice(key, storeId)
      .then((data) => {
        const product = data.list.product
        if (data.list.currency) {
          switch (data.list.currency) {
            case 'eur':
              setCurrency(' (in euros)')
              break
            case 'usd':
              setCurrency(' (in dollars)')
              break
            case 'gbp':
              setCurrency(' (in pounds)')
              break
            case 'cny':
              setCurrency(' (in yuan)')
              break
            case 'jpy':
              setCurrency(' (in yen)')
              break
            case 'aud':
              setCurrency(' (in Australian dollars)')
              break
            case 'cad':
              setCurrency(' (in Canadian dollars)')
              break
            case 'chf':
              setCurrency(' (in Swiss francs)')
              break
            case 'hkd':
              setCurrency(' (in Hong Kong dollars)')
              break
            case 'nzd':
              setCurrency(' (in New Zealand dollars)')
              break
            default:
              setCurrency('')
          }
        }
        if (product) {
          setProductData(product)
          const pricingWithKeys = (product.pricing || []).map(
            (item, index) => ({
              ...item,
              key: item.key || `pricing_${index}_${Date.now()}`,
            })
          )
          setPricing(pricingWithKeys)

          const adjustmentsWithKeys = (product.adjustments || []).map(
            (item, index) => ({
              ...item,
              key: item.key || `adjustment_${index}_${Date.now()}`,
            })
          )
          setAdjustments(adjustmentsWithKeys)
        } else {
          notification.error({ message: 'Product not found!' })
        }
      })
      .catch((error) => {
        console.error('Failed to fetch product prices:', error)
        notification.error({ message: 'Error fetching product prices!' })
      })
  }, [key, storeId, form])

  const handleAddPricing = () => {
    const newPricing = {
      key: `new_pricing_${Date.now()}`,
      label: '',
      price: 0,
    }
    setPricing([newPricing, ...pricing])
  }

  const handleRemovePricing = (pricingToRemove) => {
    Modal.confirm({
      title: 'Confirm Delete',
      content: `Are you sure you want to delete the pricing item "${
        pricingToRemove.label || pricingToRemove.key
      }"?`,
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk() {
        const newPricing = pricing.filter((p) => p.key !== pricingToRemove.key)
        setPricing(newPricing)
        notification.success({ message: 'Pricing item deleted successfully!' })
      },
    })
  }

  const handleSave = async () => {
    try {
      const values = await form.validateFields()

      const updatedPricing = pricing.map((item) => {
        const newKey = (values[`pricing_key_${item.key}`] ?? item.key)?.trim() || item.key
        const newLabel = values[`pricing_label_${item.key}`] || item.label
        const newPrice =
          values[`pricing_price_${item.key}`] !== undefined
            ? Number(values[`pricing_price_${item.key}`])
            : item.price

        return {
          ...item,
          key: newKey,
          label: newLabel,
          price: newPrice,
        }
      })

      const updatedAdjustments = adjustments.map((item) => {
        const newPrice =
          values[`adjustment_price_${item.key}`] !== undefined
            ? Number(values[`adjustment_price_${item.key}`])
            : item.price

        return {
          ...item,
          price: newPrice,
        }
      })

      const updatedProduct = {
        ...productData,
        pricing: updatedPricing,
        adjustments: updatedAdjustments,
      }

      if (JSON.stringify(updatedProduct) === JSON.stringify(productData)) {
        notification.info({ message: 'No changes detected!' })
        return
      }

      updateProductPrices(storeId, { [key]: updatedProduct })
        .then(() => {
          notification.success({
            message: 'Product price updated successfully',
          })
          history.push('/product-prices')
        })
        .catch((error) => {
          console.error('Failed to update product prices:', error)
          notification.error({ message: 'Error updating product prices' })
        })
    } catch (error) {
      console.error('Validation failed:', error)
      notification.error({ message: 'Please fill in all required fields!' })
    }
  }

  return (
    <>
      <PageHeader
        className="site-page-header"
        onBack={() => history.push('/product-prices')}
        title={`Edit ${productData?.label || ''}`}
      />
      <Row style={{ padding: '24px' }}>
        <Card style={{ width: '100%' }}>
          <Col span={24}>
            <Form form={form} layout="vertical">
              <Card
                title={`Pricing ${currency}`}
                extra={
                  <Button type="primary" onClick={handleAddPricing}>
                    Add Pricing Item
                  </Button>
                }
                style={{ marginBottom: '24px' }}
              >
                <Table
                  dataSource={pricing.map((item, index) => ({
                    ...item,
                    index,
                  }))}
                  columns={[
                    {
                      title: 'Key',
                      dataIndex: 'key',
                      key: 'key',
                      width: '25%',
                      render: (text, record) => (
                        <Form.Item
                          name={`pricing_key_${record.key}`}
                          initialValue={text}
                          rules={[
                            {
                              required: true,
                              message: 'Please enter a key (e.g. classic, premium). This is sent as quality in orders.',
                            },
                          ]}
                          style={{ margin: 0 }}
                        >
                          <Input placeholder="e.g. classic, premium" />
                        </Form.Item>
                      ),
                    },
                    {
                      title: 'Label',
                      dataIndex: 'label',
                      key: 'label',
                      width: '35%',
                      render: (text, record) => (
                        <Form.Item
                          name={`pricing_label_${record.key}`}
                          initialValue={text}
                          rules={[
                            {
                              required: true,
                              message: 'Please enter pricing label!',
                            },
                          ]}
                          style={{ margin: 0 }}
                        >
                          <Input placeholder="Enter pricing label" />
                        </Form.Item>
                      ),
                    },
                    {
                      title: 'Price',
                      dataIndex: 'price',
                      key: 'price',
                      width: '25%',
                      render: (text, record) => (
                        <Form.Item
                          name={`pricing_price_${record.key}`}
                          initialValue={text}
                          rules={[
                            {
                              required: true,
                              message: 'Please enter price!',
                            },
                            {
                              validator: (_, value) =>
                                value >= 0
                                  ? Promise.resolve()
                                  : Promise.reject(
                                      new Error('The price cannot be negative')
                                    ),
                            },
                          ]}
                          style={{ margin: 0 }}
                        >
                          <Input type="number" placeholder="Enter price" />
                        </Form.Item>
                      ),
                    },
                    {
                      title: 'Action',
                      key: 'action',
                      width: '15%',
                      render: (text, record) => (
                        <Button
                          type="link"
                          danger
                          onClick={() => handleRemovePricing(record)}
                        >
                          Remove
                        </Button>
                      ),
                    },
                  ]}
                  pagination={false}
                  rowKey="key"
                  locale={{
                    emptyText: 'No pricing items available for this product.',
                  }}
                />
              </Card>

              {adjustments.length > 0 && (
                <Card title="Adjustments">
                  <Table
                    dataSource={adjustments.map((item, index) => ({
                      ...item,
                      index,
                    }))}
                    columns={[
                      {
                        title: 'Label',
                        dataIndex: 'label',
                        key: 'label',
                        width: '40%',
                        render: (text) => text || 'N/A',
                      },
                      {
                        title: 'Price',
                        dataIndex: 'price',
                        key: 'price',
                        width: '40%',
                        render: (text, record) => (
                          <Form.Item
                            name={`adjustment_price_${record.key}`}
                            initialValue={text}
                            rules={[
                              {
                                required: true,
                                message: 'Please enter price!',
                              },
                              {
                                validator: (_, value) =>
                                  value >= 0
                                    ? Promise.resolve()
                                    : Promise.reject(
                                        new Error(
                                          'The price cannot be negative'
                                        )
                                      ),
                              },
                            ]}
                            style={{ margin: 0 }}
                          >
                            <Input type="number" placeholder="Enter price" />
                          </Form.Item>
                        ),
                      },
                      {
                        title: '',
                        key: '',
                        width: '20%',
                        render: (text, record) => <></>,
                      },
                    ]}
                    pagination={false}
                    rowKey="key"
                    locale={{
                      emptyText:
                        'No adjustment items available for this product.',
                    }}
                  />
                </Card>
              )}
            </Form>
          </Col>
          <Button key="save" type="primary" onClick={handleSave}>
            Save Changes
          </Button>
        </Card>
      </Row>
    </>
  )
}

export default EditProductPrice
